import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "./tokens";

/* Owner-flavored testimonials. SMB-first: trades, dental, legal,
   studio, agency, retail. Quotes are illustrative composites of
   beta-cohort feedback — phrased the way owners actually talk. */

type Quote = {
  name: string;
  role: string;
  roleTag: "Owner" | "Sales" | "Ops" | "Account" | "Manager" | "Partner";
  initials: string;
  hue: string; // small color cue per persona
  outcome: string; // bolded callout
  quote: string;
};

const QUOTES: Quote[] = [
  {
    name: "Elena V.", role: "Owner · 9-chair dental group",
    roleTag: "Owner",
    initials: "EV", hue: "#C4823A",
    outcome: "front desk stopped dropping recalls",
    quote: "Reception, hygiene, and billing all work off the same brain now. Recalls stopped falling through the gap between them.",
  },
  {
    name: "Tasha B.", role: "Sales lead · 8-person performance agency",
    roleTag: "Sales",
    initials: "TB", hue: "#7CB7A8",
    outcome: "shared context across reps",
    quote: "Every rep's agent sees the same customer history. No more 'who emailed Beacon last?' We close handoffs in minutes, not days.",
  },
  {
    name: "Jordan R.", role: "Managing partner · boutique law firm",
    roleTag: "Partner",
    initials: "JR", hue: "#9B7AB8",
    outcome: "real-time matter telemetry",
    quote: "I see every active matter, every deadline, every paralegal's queue in one view. CoBrain caught two billing leaks in week one.",
  },
  {
    name: "Devon M.", role: "Ops manager · 18-person field service co.",
    roleTag: "Ops",
    initials: "DM", hue: "#E8B458",
    outcome: "spotted the bottleneck same-day",
    quote: "CoBrain flagged that Tuesday installs were slipping before I noticed. The dashboard pointed at the dispatch handoff. Fixed in a week.",
  },
  {
    name: "Aisha K.", role: "Owner · 4-store specialty retail",
    roleTag: "Owner",
    initials: "AK", hue: "#C4823A",
    outcome: "every store on the same page",
    quote: "Each store manager has their own agent, but they all share the brain. End-of-day rolls up to me automatically — no chasing.",
  },
  {
    name: "Mara C.", role: "Account director · 12-person consulting firm",
    roleTag: "Account",
    initials: "MC", hue: "#7CB7A8",
    outcome: "deals stopped slipping",
    quote: "My agent talks to delivery's agent talks to finance's agent. The 'we forgot to follow up' problem is gone — CoBrain sees the whole pipeline.",
  },
];

const HERO_QUOTE = {
  name: "Marcus T.",
  role: "Owner · roofing & exteriors · 11-person crew",
  initials: "MT",
  hue: "#C4823A",
  outcome: "every employee plugged in · week one",
  quote: "We installed CoBrain on a Monday. By Friday my sales lead, dispatcher, and bookkeeper were all running their own agents — and I could see the whole company in one view for the first time in eight years.",
};

/* Services-lane variant: quotes about the engagement, not the product. */
const SERVICES_QUOTES: Quote[] = [
  {
    name: "Dana W.", role: "Owner · 6-person dental practice",
    roleTag: "Owner", initials: "DW", hue: "#7CB7A8",
    outcome: "knew what not to automate",
    quote: "Half the plan was 'don't bother.' That's the part I paid for. We automated recalls and left the rest alone.",
  },
  {
    name: "Ben O.", role: "Ops manager · plumbing & HVAC, 14 staff",
    roleTag: "Ops", initials: "BO", hue: "#C4823A",
    outcome: "first workflow live in a week",
    quote: "They didn't sell me a twelve-month roadmap. One engineer, one workflow, live the following week. Then we did the next one.",
  },
  {
    name: "Priya N.", role: "Managing partner · 9-person accounting firm",
    roleTag: "Partner", initials: "PN", hue: "#9B7AB8",
    outcome: "our own team runs it now",
    quote: "They trained our office manager while they built. Six weeks later she was shipping changes herself and we stopped paying for help.",
  },
];

const SERVICES_HERO = {
  name: "Ben O.",
  role: "Ops manager · plumbing & HVAC · 14 staff",
  initials: "BO",
  hue: "#C4823A",
  outcome: "audit to live workflow · under two weeks",
  quote: "We'd had two agencies quote us six figures and eighteen months. HireEva sent one engineer, mapped how we actually dispatch, and had the first automation running before the other quotes even came back.",
};

export const TestimonialWall = ({ variant = "cobrain" }: { variant?: "cobrain" | "services" }) => {
  const hero = variant === "services" ? SERVICES_HERO : HERO_QUOTE;
  const quotes = variant === "services" ? SERVICES_QUOTES : QUOTES;
  return (
  <div data-testimonial-wall style={{ display: "flex", flexDirection: "column", gap: 20 }}>
    {/* Hero named quote — anchors the wall */}
    <article style={{
      background: PALETTE.dark, color: "#fff",
      borderRadius: 24, padding: "clamp(28px, 4vw, 40px)",
      display: "grid", gridTemplateColumns: "minmax(0, 0.95fr) auto",
      gap: 24, alignItems: "center", position: "relative", overflow: "hidden",
    }} className="cb-hero-quote">
      <style>{`
        @media (max-width: 760px) {
          .cb-hero-quote { grid-template-columns: 1fr !important; gap: 16px !important; }
        }
      `}</style>
      <div aria-hidden style={{
        position: "absolute", inset: "-30% -10% auto auto", width: 480, height: 480,
        background: `radial-gradient(circle, ${hero.hue}33 0%, transparent 60%)`,
        filter: "blur(60px)", pointerEvents: "none",
      }} />
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
        <span style={{
          alignSelf: "flex-start",
          padding: "4px 10px", borderRadius: 999,
          background: "rgba(196,130,58,0.16)", border: "1px solid rgba(196,130,58,0.35)",
          fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.14em",
          textTransform: "uppercase", color: PALETTE.amberSoft, fontWeight: 700,
          maxWidth: "100%", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35,
        }}>{hero.outcome}</span>
        <p style={{
          fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: "clamp(22px, 2.6vw, 32px)",
          lineHeight: 1.25, color: "#fff", margin: 0, letterSpacing: "-0.01em",
        }}>
          &ldquo;{hero.quote}&rdquo;
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
          <span aria-hidden style={{
            width: 44, height: 44, borderRadius: "50%",
            background: `linear-gradient(135deg, ${hero.hue}, ${hero.hue}aa)`,
            color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 16, fontWeight: 400,
          }}>{hero.initials}</span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: "#fff" }}>{hero.name}</span>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>{hero.role}</span>
          </div>
        </div>
      </div>
    </article>

    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: 16,
    }}>
    {quotes.map((q) => (
      <article key={q.name} style={{
        background: PALETTE.card, border: `1px solid ${PALETTE.line}`,
        borderRadius: 20, padding: 24, display: "flex", flexDirection: "column", gap: 14,
        position: "relative", overflow: "hidden",
      }}>
        <div aria-hidden style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent, ${q.hue}, transparent)`,
          opacity: 0.55,
        }} />
        <div style={{
          display: "inline-flex", alignSelf: "flex-start",
          padding: "4px 10px", borderRadius: 999,
          background: `${q.hue}1A`, border: `1px solid ${q.hue}40`,
          fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.14em",
          textTransform: "uppercase", color: q.hue, fontWeight: 700,
          maxWidth: "100%", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35,
        }}>{q.outcome}</div>
        <span style={{
          alignSelf: "flex-start",
          padding: "2px 8px", borderRadius: 999,
          background: "rgba(0,0,0,0.04)", border: `1px solid ${PALETTE.line}`,
          fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.10em",
          textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700,
        }}>{q.roleTag} voice</span>
        <p style={{
          fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.55,
          color: PALETTE.ink, margin: 0,
        }}>
          &ldquo;{q.quote}&rdquo;
        </p>
        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 12, paddingTop: 12, borderTop: `1px solid ${PALETTE.line}` }}>
          <span aria-hidden style={{
            width: 36, height: 36, borderRadius: "50%",
            background: `linear-gradient(135deg, ${q.hue}, ${q.hue}aa)`,
            color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 14, fontWeight: 400,
            letterSpacing: "0.02em", flexShrink: 0,
          }}>{q.initials}</span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: PALETTE.ink }}>{q.name}</span>
            <span style={{ fontSize: 12, color: PALETTE.muted, overflow: "hidden", textOverflow: "ellipsis" }}>{q.role}</span>
          </div>
        </div>
      </article>
    ))}
    </div>
  </div>
  );
};
